import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ShieldCheck, Sparkles, Users, Heart, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.unsplash.com/photo-1775664377581-7ca46ddb619b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const CARD_IMG_1 = "https://images.unsplash.com/photo-1619715613791-89d35b51ff81?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";
const CARD_IMG_2 = "https://images.unsplash.com/photo-1775582837346-428dea700f53?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

export default function Home() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero */}
      <section className="relative overflow-hidden hero-grain">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center py-12 lg:py-20">
          <div className="lg:col-span-6 animate-fade-up">
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.1]">
              Where <span className="text-[#6B1D2F] gold-underline">NRI</span> hearts find their forever.
            </h1>
            <p className="mt-6 text-lg text-slate-700 leading-relaxed max-w-xl">
              Verified NRI profiles from across the world — searchable by visa type, community, and expiry date — with a dedicated match-maker option for those who prefer white-glove service.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {user ? (
                <>
                  <Link to="/search" data-testid="hero-cta-search">
                    <Button className="bg-[#6B1D2F] hover:bg-[#541624] text-white px-8 py-6 rounded-full text-base font-semibold shadow-lg hover:shadow-xl">Search Profiles</Button>
                  </Link>
                  <Link to="/matches" data-testid="hero-cta-matches">
                    <Button variant="outline" className="border-2 border-[#6B1D2F] text-[#6B1D2F] hover:bg-[#6B1D2F]/5 px-8 py-6 rounded-full text-base font-semibold">My Matches</Button>
                  </Link>
                </>
              ) : (
                <>
                  <Link to="/register" data-testid="hero-cta-register">
                    <Button className="bg-[#6B1D2F] hover:bg-[#541624] text-white px-8 py-6 rounded-full text-base font-semibold shadow-lg hover:shadow-xl">Create Free Profile</Button>
                  </Link>
                  <Link to="/pricing" data-testid="hero-cta-pricing">
                    <Button variant="outline" className="border-2 border-[#6B1D2F] text-[#6B1D2F] hover:bg-[#6B1D2F]/5 px-8 py-6 rounded-full text-base font-semibold">See Plans</Button>
                  </Link>
                </>
              )}
            </div>
            <div className="mt-10 flex flex-wrap gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-emerald-700" /> Visa-verified profiles</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-emerald-700" /> 100% NRI focused</div>
              <div className="flex items-center gap-2"><Award className="w-4 h-4 text-emerald-700" /> Human match makers</div>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-white">
              <img src={HERO_IMG} alt="Elegant Indian wedding" className="w-full h-[420px] lg:h-[520px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#6B1D2F]/40 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur rounded-xl p-4 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#6B1D2F] text-white flex items-center justify-center font-serif text-lg">A</div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Anjali & Vikram — matched Oct 2025</p>
                    <p className="text-xs text-slate-500">Cardiologist × AI Researcher · Verified match</p>
                  </div>
                </div>
              </div>
            </div>
            <Badge className="absolute -top-3 -left-3 bg-[#D4AF37] text-slate-900 font-semibold px-4 py-2 rounded-full shadow-lg">4.9 ★ Trusted</Badge>
          </div>
        </div>
      </section>

      {/* Bento features */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-2xl mb-10">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#B8860B] mb-3">Why EliteNRIMatrimony</p>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900">Built for the Indian diaspora abroad.</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-6">
          <div className="md:col-span-5 rounded-2xl border border-[#EAE3D2] bg-white p-8 shadow-sm">
            <ShieldCheck className="w-8 h-8 text-emerald-700" />
            <h3 className="mt-4 font-serif text-2xl text-slate-900">Visa-first search</h3>
            <p className="mt-2 text-slate-600">Filter by Skilled Worker, Global Talent, Student, ILR, HSMP and expiry windows so you meet people whose plans align with yours.</p>
          </div>
          <div className="md:col-span-3 rounded-2xl border border-[#EAE3D2] bg-[#6B1D2F] p-8 text-white shadow-sm">
            <Sparkles className="w-8 h-8 text-[#D4AF37]" />
            <h3 className="mt-4 font-serif text-2xl">AI Match Maker</h3>
            <p className="mt-2 text-white/85 text-sm">Claude-powered compatibility insights for Elite members. Curated, private, weekly.</p>
          </div>
          <div className="md:col-span-4 rounded-2xl border border-[#EAE3D2] bg-white overflow-hidden shadow-sm">
            <img src={CARD_IMG_1} alt="Bride" className="w-full h-40 object-cover" />
            <div className="p-6">
              <h3 className="font-serif text-xl text-slate-900">Community & caste</h3>
              <p className="mt-2 text-slate-600 text-sm">Brahmin, Kshatriya, Nair, Iyer, Reddy, Patel, Jat and more — respected and searchable.</p>
            </div>
          </div>
          <div className="md:col-span-4 rounded-2xl border border-[#EAE3D2] bg-white p-8 shadow-sm">
            <Users className="w-8 h-8 text-[#6B1D2F]" />
            <h3 className="mt-4 font-serif text-2xl text-slate-900">Family in the loop</h3>
            <p className="mt-2 text-slate-600">Home-country contact details for both sides, respectfully masked until you upgrade.</p>
          </div>
          <div className="md:col-span-4 rounded-2xl border border-[#EAE3D2] bg-white overflow-hidden shadow-sm">
            <img src={CARD_IMG_2} alt="Professional NRI woman" className="w-full h-40 object-cover" />
            <div className="p-6">
              <h3 className="font-serif text-xl text-slate-900">Verified professionals</h3>
              <p className="mt-2 text-slate-600 text-sm">Doctors, engineers, bankers, researchers — the global Indian diaspora in one place.</p>
            </div>
          </div>
          <div className="md:col-span-4 rounded-2xl border border-[#EAE3D2] bg-white p-8 shadow-sm">
            <CheckCircle2 className="w-8 h-8 text-emerald-700" />
            <h3 className="mt-4 font-serif text-2xl text-slate-900">Two-step secure login</h3>
            <p className="mt-2 text-slate-600">Password plus a 6-digit PIN sent to your email. Your data stays yours.</p>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-white border-y border-[#EAE3D2] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900 mb-10">A journey befitting your story</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: "01", t: "Register free", d: "Share your personal, education, professional, community and visa details. Verified & masked." },
              { n: "02", t: "Search & shortlist", d: "Filter by visa type, community, and age. Unlock contact & chat from £199, immigration details from £399." },
              { n: "03", t: "Match with confidence", d: "Upgrade to £699 for a human match-maker plus AI-powered weekly insights." },
            ].map((s) => (
              <div key={s.n} className="rounded-2xl border border-[#EAE3D2] bg-[#FDFBF7] p-8">
                <div className="text-[#D4AF37] font-serif text-4xl">{s.n}</div>
                <h3 className="mt-3 font-serif text-2xl text-slate-900">{s.t}</h3>
                <p className="mt-2 text-slate-600">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      {!user && (
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <img src="/logo-emblem.png" alt="EliteNRIMatrimony" data-testid="footer-cta-logo" className="h-20 sm:h-24 w-auto mx-auto object-contain" />
        <h2 className="mt-4 font-serif text-3xl sm:text-4xl font-semibold text-slate-900">Your best person is a search away.</h2>
        <p className="mt-3 text-slate-600 max-w-xl mx-auto">Join thousands of NRIs already writing their next chapter with us.</p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/register" data-testid="footer-cta-register">
            <Button className="bg-[#6B1D2F] hover:bg-[#541624] text-white px-8 py-6 rounded-full text-base font-semibold shadow-lg">Create Free Profile</Button>
          </Link>
        </div>
      </section>
      )}

      <footer className="border-t border-[#EAE3D2] py-8 text-center text-sm text-slate-500">
        © 2026 EliteNRIMatrimony ·{" "}
        <Link to="/privacy" data-testid="footer-privacy-link" className="underline hover:text-[#6B1D2F]">Privacy Policy</Link> ·{" "}
        <Link to="/terms" data-testid="footer-terms-link" className="underline hover:text-[#6B1D2F]">Terms &amp; Conditions</Link>
      </footer>
    </div>
  );
}
